/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon May 27 2024
 **/
import { useMemo } from "react";
import { AccordionTemplate, Grid, Icon, Row, TypographyTooltip } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { Avatar } from "@/frameworks/core/components";
import * as S from "./emotion";
import { colors } from "@/frameworks/core/styles";

const ELLIPSIS_OFFSET = 20;

export default function PhoneGroupPeople({
	id,
	title,
	list,
	onUserSelected,
	selectedUserList
}: {
	id: string;
	title: string;
	list: WebServices.People.List.Item["users"];
	onUserSelected: (userID: string, name: string) => void;
	selectedUserList: WebServices.User.UserInfo[];
}) {
	const selectedUserMap = useMemo(() => {
		const map = new Map<string, number>();
		selectedUserList.forEach((user, index) => {
			map.set(user.userID, index + 1);
		});
		return map;
	}, [selectedUserList]);

	const getBackgroundColor = (userId: string) => {
		const position = selectedUserMap.get(userId);
		if (position === 2 || position === 3) return colors.gradient_40_100_w60;
		return undefined;
	};

	return (
		<AccordionTemplate
			container={{
				activeId: id
			}}
			accordion={{
				id,
				style: {
					padding: "9px 0",
					width: "100%",
					height: "fit-content",
					overflow: "auto"
				}
			}}
			header={{
				render: ({ isAccordionOpen }) => (
					<Row
						verticalAlign="center"
						horizonAlign="distribute"
						css={{ padding: "0 12px", paddingBottom: "6px" }}
					>
						<Row verticalAlign="center" css={{ width: `calc(100% - ${ELLIPSIS_OFFSET}px)` }}>
							<Maybe test={isAccordionOpen} fallback={<Icon name="RightFilled16" size={16} />}>
								<Icon name="DownFilled16" size={16} />
							</Maybe>
							<Row verticalAlign="center" css={{ width: `calc(100% - ${ELLIPSIS_OFFSET}px)` }}>
								<TypographyTooltip.p
									tooltipProps={{ hasIcon: false }}
									componentProps={{
										maxLine: 1,
										ellipsis: true,
										style: { paddingLeft: "4px" },
										stretch: true,
										typoSize: "Title3"
									}}
								>
									{`${title} (${list?.length})`}
								</TypographyTooltip.p>
							</Row>
						</Row>
					</Row>
				)
			}}
			body={{
				render: () => (
					<Grid.ul column={4} gridGap={2} style={{ placeItems: "center" }}>
						{sortByStatus(list)?.map((item) => {
							const backgroundColor = getBackgroundColor(item.userId);

							return (
								<S.GridUserItemStyled
									key={item.userId}
									backgroundColor={backgroundColor}
									horizonAlign="center"
									onClick={() => onUserSelected(item.userId, item.name || "")}
								>
									<Avatar id={item.userId} kind={"people_48"} showMiniProfile={false} />
									<Row horizonAlign="center" css={{ width: "100%", overflow: "hidden" }}>
										<TypographyTooltip.p
											tooltipProps={{ hasIcon: false }}
											componentProps={{
												ellipsis: true,
												maxLine: 1,
												typoColor: "neutral_70_100",
												align: "center",
												typoSize: "Body6"
											}}
										>
											{item.name}
										</TypographyTooltip.p>
									</Row>
								</S.GridUserItemStyled>
							);
						})}
					</Grid.ul>
				)
			}}
		/>
	);
}

function sortByStatus(list: WebServices.People.List.Item["users"]) {
	return list?.sort((a, b) => {
		if (a.activeStatus === "ACTIVE" && b.activeStatus !== "ACTIVE") {
			return -1;
		} else if (a.activeStatus !== "ACTIVE" && b.activeStatus === "ACTIVE") {
			return 1;
		} else {
			return 0;
		}
	});
}
